import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tooltip } from '@mui/material';
import { colors } from '../../theme/colors';

export type EntityReferenceKind = 'organization' | 'guide' | 'plan';

interface EntityReferenceBaseProps {
  kind: EntityReferenceKind;
  label: string;
  tooltipTitle?: string;
}

interface OrganizationRefProps extends EntityReferenceBaseProps {
  kind: 'organization';
  orgSlug?: string;
  orgId?: string;
}

interface GuideRefProps extends EntityReferenceBaseProps {
  kind: 'guide';
  guideId?: string;
  orgSlug?: string;
  guideSlug?: string;
}

interface PlanRefProps extends EntityReferenceBaseProps {
  kind: 'plan';
  planId: string;
}

export type EntityReferenceProps =
  | OrganizationRefProps
  | GuideRefProps
  | PlanRefProps;

const defaultTooltips: Record<EntityReferenceKind, string> = {
  organization: 'Organization · Click to open settings',
  guide: 'Guide · Click to open',
  plan: 'Plan · Click to open',
};

function getHref(props: EntityReferenceProps): string | null {
  switch (props.kind) {
    case 'organization':
      return '/org/settings';
    case 'guide':
      if (props.guideId) return `/org/guides/${props.guideId}/edit`;
      if (props.orgSlug && props.guideSlug) return `/#/o/${props.orgSlug}/g/${props.guideSlug}`;
      return null;
    case 'plan':
      return `/app/plans/${props.planId}`;
    default:
      return null;
  }
}

/**
 * Inline referential entity: Organization, Guide, or Plan.
 * Renders as a purple pill-style link with tooltip; click navigates to the entity.
 * Use when entity names appear in prose and should be visually distinct and interactive.
 */
export const EntityReference: React.FC<EntityReferenceProps> = (props) => {
  const { kind, label, tooltipTitle } = props;
  const href = getHref(props);
  const tooltip = tooltipTitle ?? defaultTooltips[kind];

  const pillSx = {
    color: colors.purple[600],
    fontWeight: 500,
    bgcolor: colors.purple[50],
    border: `1px solid ${colors.purple[100]}`,
    borderRadius: 1,
    px: 0.75,
    py: 0.25,
    textDecoration: 'none',
    cursor: href ? 'pointer' : 'default',
    display: 'inline',
    '&:hover': {
      bgcolor: colors.purple[100],
      textDecoration: href ? 'underline' : 'none',
    },
  };

  if (!label?.trim()) {
    return null;
  }

  const content = href ? (
    <Link
      component={RouterLink}
      to={href}
      sx={pillSx}
      underline="none"
      variant="body2"
    >
      {label}
    </Link>
  ) : (
    <span style={{ color: colors.purple[600], fontWeight: 500 }}>{label}</span>
  );

  return (
    <Tooltip title={tooltip} enterDelay={300}>
      <span>{content}</span>
    </Tooltip>
  );
};

export default EntityReference;
